import React from 'react';
import { useTranslation } from 'react-i18next';
import { Carousel } from '../Carousel';
import { Image as FiImage } from '../Image';
import { Species } from '../../types/gql-op-types';
import type { PickYourFiDeviceKey } from './HardwareFirstBelowFold';
import styles from './HardwareFirstAppCarousel.module.scss';

// live tab, health tab, map - the same three app screens shot per device (ultra has no health tab)
const MINI_APP_SCREENS = [
  '/product_images/mini/app-ui-live.png',
  '/product_images/mini/app-ui-health.png',
  '/product_images/mini/app-ui-map.png',
];

const S3_APP_SCREENS = [
  '/product_images/smart-collar-v3b/app-ui-live.png',
  '/product_images/smart-collar-v3b/app-ui-health.png',
  '/product_images/smart-collar-v3b/app-ui-map.png',
];

const ULTRA_APP_SCREENS = ['/product_images/ultra/app-ui-live.png', '/product_images/ultra/app-ui-map.png'];

const CAT_APP_SCREENS = ['/product_images/smart-collar-v3b/app-ui-homescreen-1-cat.png'];

const APP_SCREENS: Record<PickYourFiDeviceKey, string[]> = {
  mini: MINI_APP_SCREENS,
  s3: S3_APP_SCREENS,
  ultra: ULTRA_APP_SCREENS,
};

const DEVICE_NAMES: Record<PickYourFiDeviceKey, string> = {
  mini: 'Fi Mini',
  s3: 'Fi Series 3+',
  ultra: 'Fi Ultra',
};

/**
 * The app screenshots below the fold, one treatment for all three devices. Each device has its
 * own set of the same screens, so the tracker in the shots matches the one being sold. Cats get
 * the cat home screen and none of the dog screens, which read dog activity.
 */
export default function HardwareFirstAppCarousel({
  deviceKey,
  species = Species.DOG,
  solo = false,
}: {
  deviceKey: PickYourFiDeviceKey;
  species?: Species;
  /** The solo page shows the screenshots without a heading; the section keeps it as its label. */
  solo?: boolean;
}) {
  const { t } = useTranslation();
  const images = species === Species.CAT ? CAT_APP_SCREENS : APP_SCREENS[deviceKey];

  const deviceName = species === Species.CAT ? 'Fi Mini for Cats' : DEVICE_NAMES[deviceKey];

  const heading = t('hardware_first_see_device_in_action', { device: deviceName });
  return (
    <section aria-label={solo ? heading : undefined}>
      {!solo && <h2 className={styles.heading}>{heading}</h2>}
      <Carousel className={styles.carousel} wrapAround prevNextButtons={images.length > 1}>
        {images.map((url) => (
          <div key={url} className={styles.image}>
            <FiImage
              image={{ type: 'image', url, descriptors: [species === Species.CAT ? '1000w' : '648w'] }}
              loading="eager"
            />
          </div>
        ))}
      </Carousel>
    </section>
  );
}
